/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import debounce from "lodash.debounce";
import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import Map, { MapRef, ViewStateChangeEvent } from "react-map-gl/maplibre";
import { Box } from "@mui/material";
import { appChromeTop } from "../Header/headerMetrics";
import { StopPlaceActions, UserActions } from "../../../actions";
import { getNeighbourStops } from "../../../actions/TiamatActions.modern";
import { ConfigContext, MapConfig } from "../../../config/ConfigContext";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { AddElementFab } from "./controls/AddElementFab";
import { MapControls } from "./controls/MapControls";
import { useMapComponentLayers } from "./hooks/useMapComponentLayers";
import { useMapCurrentStop } from "./hooks/useMapCurrentStop";
import { maplibreLib } from "./maplibreLib";
import { FareZonesLayer } from "./layers/FareZonesLayer";
import { GroupEdgesLayer } from "./layers/GroupEdgesLayer";
import { MultimodalEdgesLayer } from "./layers/MultimodalEdgesLayer";
import { PathLinkLayer } from "./layers/PathLinkLayer";
import { StopGroupLayer } from "./layers/StopGroupLayer";
import { TariffZonesLayer } from "./layers/TariffZonesLayer";
import { BoardingPositionMarkers } from "./markers/BoardingPositionMarkers";
import { NeighbourMarkers } from "./markers/NeighbourMarkers";
import { NeighbourQuayMarkers } from "./markers/NeighbourQuayMarkers";
import { ParkingMarkers } from "./markers/ParkingMarkers";
import { QuayMarkers } from "./markers/QuayMarkers";
import { StopPlaceMarker } from "./markers/StopPlaceMarker";
import { buildMaplibreStyle } from "./tile-sources/buildMaplibreStyle";

const NEIGHBOUR_STOPS_MIN_ZOOM = 13;
const STOP_PLACE_FLY_TO_ZOOM = 17;
const GROUP_FLY_TO_ZOOM = 16;

const DEFAULT_MAP_CONFIG: MapConfig = {
  baseLayers: [
    {
      name: "OpenStreetMap",
      url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    },
  ],
  defaultBaseLayer: "OpenStreetMap",
  center: [64.349421, 16.809082],
  zoom: 6,
};

export const ModernEditStopMap = () => {
  const dispatch = useAppDispatch();
  const mapRef = useRef<MapRef>(null);
  const { mapConfig } = useContext(ConfigContext);
  const config = mapConfig ?? DEFAULT_MAP_CONFIG;

  const centerPosition = useAppSelector(
    (state) => state.stopPlace.centerPosition as [number, number],
  );
  const zoom = useAppSelector((state) => state.stopPlace.zoom as number);
  const activeBaseLayer = useAppSelector(
    (state) => (state.user as any).activeBaselayer as string,
  );

  // Group-aware (F-88): in the group view the previous stop is a neighbour
  // again, so it is no longer left out of the neighbour query.
  const currentStop = useMapCurrentStop();
  const currentStopId = currentStop?.id;
  const currentLocation = currentStop?.location;
  const currentGroupId = useAppSelector(
    (state) =>
      (state as any).stopPlacesGroup?.current?.id as string | undefined,
  );
  const groupMemberCount = useAppSelector(
    (state) =>
      ((state as any).stopPlacesGroup?.current?.members?.length ?? 0) as number,
  );
  const groupCenterPosition = useAppSelector(
    (state) =>
      (state as any).stopPlacesGroup?.centerPosition as
        [number, number] | undefined,
  );
  const showExpiredStops = useAppSelector(
    (state) => (state.stopPlace as any).showExpiredStops as boolean,
  );

  const { resolvedComponentUrl, transformRequest } = useMapComponentLayers(
    config,
    activeBaseLayer,
  );

  // Ref so the stable debounce callback always reads the latest values
  const neighbourStateRef = useRef({ currentStopId, showExpiredStops });
  useEffect(() => {
    neighbourStateRef.current = { currentStopId, showExpiredStops };
  }, [currentStopId, showExpiredStops]);

  // Re-fetch neighbour stops immediately when showExpiredStops changes,
  // in case the map is already zoomed in and the user is not moving it.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (map.getZoom() <= NEIGHBOUR_STOPS_MIN_ZOOM) return;
    dispatch(
      getNeighbourStops(
        neighbourStateRef.current.currentStopId,
        map.getBounds(),
        showExpiredStops,
      ),
    );
    // currentStopId intentionally excluded — this only reacts to the toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExpiredStops, dispatch]);

  // The open stop is left out of the neighbour query because the editor draws
  // it. When the editor closes or switches stop, that stop has to come back
  // as a neighbour now, not on the next pan (F-61).
  const previousStopIdRef = useRef(currentStopId);
  useEffect(() => {
    const previousStopId = previousStopIdRef.current;
    previousStopIdRef.current = currentStopId;
    if (!previousStopId || previousStopId === currentStopId) return;

    const map = mapRef.current?.getMap();
    if (!map || map.getZoom() <= NEIGHBOUR_STOPS_MIN_ZOOM) return;
    dispatch(
      getNeighbourStops(
        currentStopId,
        map.getBounds(),
        neighbourStateRef.current.showExpiredStops,
      ),
    );
  }, [currentStopId, dispatch]);

  const initialViewState = useMemo(
    () => ({
      latitude: centerPosition[0],
      longitude: centerPosition[1],
      zoom,
    }),
    // Intentionally only used as the one-time initial value — not reactive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleMapLoad = useCallback(() => {
    if (mapRef.current) {
      dispatch(StopPlaceActions.setActiveMap(mapRef.current.getMap()));
    }
  }, [dispatch]);

  const handleMoveEnd = useMemo(
    () =>
      debounce((event: ViewStateChangeEvent) => {
        const { latitude, longitude, zoom: newZoom } = event.viewState;
        dispatch(UserActions.setCenterAndZoom([latitude, longitude], newZoom));

        if (newZoom > NEIGHBOUR_STOPS_MIN_ZOOM) {
          const map = mapRef.current?.getMap();
          if (map) {
            const {
              currentStopId: ignoreId,
              showExpiredStops: includeExpired,
            } = neighbourStateRef.current;
            dispatch(
              getNeighbourStops(ignoreId, map.getBounds(), includeExpired),
            );
          }
        } else {
          dispatch(UserActions.removeStopsNearbyForOverview());
        }
      }, 500),
    // mapRef and neighbourStateRef are stable refs — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  useEffect(() => {
    return () => {
      handleMoveEnd.cancel();
    };
  }, [handleMoveEnd]);

  useEffect(() => {
    if (!currentStopId || !currentLocation || !mapRef.current) return;
    const [lat, lng] = currentLocation;
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: STOP_PLACE_FLY_TO_ZOOM,
      duration: 800,
    });
  }, [currentStopId]);

  useEffect(() => {
    if (!currentGroupId || !groupCenterPosition || !mapRef.current) return;
    // A group without members has no centre of its own; the reducer falls
    // back to the config default, so flying there left a blank map (F-83).
    if (groupMemberCount === 0) return;
    const [lat, lng] = groupCenterPosition;
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: GROUP_FLY_TO_ZOOM,
      duration: 800,
    });
    // groupCenterPosition is included so re-navigating to the same group (same currentGroupId)
    // still triggers flyTo — the reducer always produces a new array reference on fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId, groupCenterPosition]);

  const mapStyle = useMemo(
    () => buildMaplibreStyle(config, activeBaseLayer, resolvedComponentUrl),
    [config, activeBaseLayer, resolvedComponentUrl],
  );

  const activeLayerMaxZoom = useMemo(() => {
    const layer =
      config.baseLayers.find((l) => l.name === activeBaseLayer) ??
      config.baseLayers[0];
    return layer?.maxZoom ?? 20;
  }, [config, activeBaseLayer]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: appChromeTop,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }}
    >
      <Map
        ref={mapRef}
        mapLib={maplibreLib}
        initialViewState={initialViewState}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyle}
        transformRequest={transformRequest}
        onLoad={handleMapLoad}
        onMoveEnd={handleMoveEnd}
        maxZoom={activeLayerMaxZoom}
        pitchWithRotate={false}
        dragRotate={false}
      >
        <MapControls />
        <AddElementFab />
        <FareZonesLayer />
        <TariffZonesLayer />
        <StopGroupLayer />
        <MultimodalEdgesLayer />
        <GroupEdgesLayer />
        <PathLinkLayer />
        <NeighbourMarkers />
        <NeighbourQuayMarkers />
        <StopPlaceMarker />
        <QuayMarkers />
        <ParkingMarkers />
        <BoardingPositionMarkers />
      </Map>
    </Box>
  );
};
